import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { Tab } from '../types';
import DashboardGrid from './DashboardGrid';
import { useDashboard } from '../hooks/useDashboard';

interface TabPanelProps {
  tab: Tab;
  isActive: boolean;
  children?: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({
  tab,
  isActive,
  children
}) => {
  const { config, editMode } = useDashboard();
  
  if (!isActive) {
    return null;
  }

  // Get the current tab data from config instead of relying on the passed tab prop
  // This ensures we always have the latest groups data after drag-and-drop updates
  const currentTab = config?.tabs?.find(t => t.id === tab.id) || tab;

  return (
    <Box
      role="tabpanel"
      id={`tabpanel-${tab.id}`}
      aria-labelledby={`tab-${tab.id}`}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0 // Important for flex container
      }}
    >
      {currentTab.groups && currentTab.groups.length > 0 ? (
        <DashboardGrid 
          groups={currentTab.groups}
          layout={config?.layout || {
            headerTitle: '',
            headerIcon: 'dashboard',
            showSearch: true,
            tilesPerRow: 4,
            groupSpacing: 24,
            tileSpacing: 16
          }}
          theme={config?.theme || {
            mode: 'light',
            primaryColor: '#1976d2',
            secondaryColor: '#dc004e',
            backgroundColor: '#f5f5f5',
            textColor: '#333333',
            cardBackground: '#ffffff',
            borderRadius: 8,
            customColors: {
              accent1: '#ff9800',
              accent2: '#4caf50'
            }
          }}
          editMode={editMode}
        />
      ) : (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box
            sx={{
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="h6" gutterBottom>
              No groups in this tab yet
            </Typography>
            <Typography variant="body2">
              Switch to edit mode to add groups and tiles to this tab.
            </Typography>
          </Box>
        </Container>
      )}
      {children}
    </Box>
  );
};

export default TabPanel;